import '../../styles/root.scss';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FilterLogicProvider } from '../filter/FilterLogicProvider';

import {
	getUserID,
	getAgencyID,
	setApiUserReady,
	setUserId,
	setUserTariff,
	setUserTariffSlug,
	setUserType,
	setReadyToOrder,
	setReadyToVideochat,
	setFavoriteCounter,
	setBookingAmount,
	setEmailVerifyAt,
	setIsLkPage,
	setUserBalance,
	setUserHold,
	setUserStatus,
	setUserTickets,
	setVideochatAmount,
	setUserAgencyId,
	setVideochatStatus,
	setCity,
	setCountriesData,
	setCurrentCity,
	setLang,
	setLanguagesWords,
	setNationalistWords,
	setServicesWords,
	setCountrySlug,
	getIncomingTicket,
	setUserCurrentDistrict,
} from '@/stores/slices/userSlice';
import {
	setDefaultRangeState,
	setDisabledFilters,
	toggleSomeFilter,
} from '@/stores/slices/filterSlice';
import { Suspense, useEffect, lazy, useCallback } from 'react';
import useLadyService from '@/services/LadyService';
import { setProfileStatus } from '@/stores/slices/userProfileSlice';
import {
	setAllModelsMeta,
	setDisabledModels,
	setDisabledModelsMeta,
} from '@/stores/slices/modelsState';
import { setAgencies, setAgency } from '@/stores/slices/agencySlice';
import { setPreviewsAmount, setRegion } from '@/stores/slices/userSlice';
import {
	setTitleArray,
	clearTitle,
	setSeoTitle,
	setSeoDescription,
	setSeoContent,
} from '@/stores/slices/titleSlice';
import {
	setProfileData,
	setSortedServices,
	setTopProfilesData,
} from '@/stores/slices/oneProfileSlice';
import { makeRequest } from '@/services/makeRequest';

import { setAllModels, setSelections } from '@/stores/slices/modelsState';
import { Loader } from '@/components/ui';
import { ScrollToTop } from '../ui/ScrollToTop';
import PrivateRoute from '../private-route/private-route';

import SignIn from '../signIn/SignIn';
import MainLayout from '../pages/Layouts/MainLayout';
import SupportPage from '../pages/support/SupportPage';
import ComingSoon from '../pages/ComingSoon';
import RedirectLk from '@/components/pages/redirectLk/RedirectLk';

import { setPost, setPosts } from '@/stores/slices/blogSlice';

import { ActivateProfilePopup } from '../popups';
import MainPage from '../pages/MainPage';
import Selection from '../pages/selection/Selection';
import ErrorPage from '../pages/ErrorPage';

// ─── PRIVATE PAGES (lazy - only load when user is authenticated) ────────────

const ReviewsPage = lazy(
	() =>
		import(
			/* webpackChunkName: "lk-only" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/reviews/ReviewsPage'
		),
);
const VideochatHistoryPage = lazy(
	() =>
		import(
			/* webpackChunkName: "lk-only" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/videochatHistoryPage/VideochatHistoryPage'
		),
);
const ChangePartyPage = lazy(
	() =>
		import(
			/* webpackChunkName: "lk-only" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/partyPage/ChangePartyPage'
		),
);
const ProfilesPage = lazy(
	() =>
		import(
			/* webpackChunkName: "lk-only" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/accounts/model/profiles/ProfilesPage'
		),
);
const CreateProfile = lazy(
	() =>
		import(
			/* webpackChunkName: "lk-only-create" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/accounts/createProfile/CreateProfile'
		),
);
const ReservationPage = lazy(
	() =>
		import(
			/* webpackChunkName: "lk-only" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/reservations/ReservationPage'
		),
);
const BalancePage = lazy(
	() =>
		import(
			/* webpackChunkName: "lk-only" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/balancePage/BalancePage'
		),
);
const StatisticPage = lazy(
	() =>
		import(
			/* webpackChunkName: "lk-only-stats" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/statistic/StatisticPage'
		),
);
const Balance = lazy(
	() =>
		import(
			/* webpackChunkName: "lk-only" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/balance/Balance'
		),
);
const WitchdrawBalance = lazy(
	() =>
		import(
			/* webpackChunkName: "lk-only" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '@/components/pages/witchdrawBalance/WitchdrawBalance'
		),
);
const Subscriptions = lazy(
	() =>
		import(
			/* webpackChunkName: "lk-only" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '@/components/pages/subscriptions/Subscriptions'
		),
);
const OperatorsPage = lazy(
	() =>
		import(
			/* webpackChunkName: "lk-only" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '@/components/pages/operators/OperatorsPage'
		),
);
const AccountPage = lazy(
	() =>
		import(
			/* webpackChunkName: "lk-only" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '@/components/pages/accounts/AccountPage'
		),
);
const PreviewsPage = lazy(
	() =>
		import(
			/* webpackChunkName: "lk-only" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '@/components/pages/previews/PreviewsPage'
		),
);
const PartiesPage = lazy(
	() =>
		import(
			/* webpackChunkName: "lk-only" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '@/components/pages/partiesPage/PartiesPage'
		),
);

// LIKELY NOT VISITED IN FIRST SEESSION PAGE
const OperatorConfirm = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/operatorConfirm/OperatorConfirm'
		),
);

const RegistrationPage = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '@/components/pages/registrationPage/RegistrationPage'
		),
);
const UIKitPage = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/uIKitPage/UIKitPage'
		),
);
const About = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/about/About'
		),
);
const RulesPage = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/rules/RulesPage'
		),
);
const PrivacyPolicy = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/privacyPolicy/PrivacyPolicy'
		),
);
const Guide = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/guide/Guide'
		),
);
const Blogs = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/blog/Blogs'
		),
);
const Blog = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/blog/Blog'
		),
);
const AgencyList = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/agencyList/AgencyList'
		),
);
const AgencyPage = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/agency/AgencyPage'
		),
);
const GlobalBlockPage = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/disabledAdsPage/disabedAdsPage'
		),
);
const ProvisionsPage = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '@/components/pages/userDeclaration/ProvisionsPage'
		),
);
const AdminLogin = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '@/components/pages/adminLogin/AdminLogin'
		),
);
const History = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '@/components/pages/history/History'
		),
);
const MassageList = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/accounts/components/massageList/MassageList'
		),
);
const PartyPage = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '../pages/partyPage/PartyPage'
		),
);
const VariantModelPage = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary-mpage" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '@/components/pages/modelPage/VariantModelPage.js'
		),
);

const PartnerLinksPage = lazy(
	() =>
		import(
			/* webpackChunkName: "public-secondary-mpage" */ /* webpackPrefetch: false */
			/* webpackPreload: false */ '@/components/pages/PartnerLinksPage.js'
		),
);

function App({ initialData }) {
	const { dispatch, userType, token, lang, navigate, t, theme } =
		useLadyService();

	const isSSR = typeof window === 'undefined';
	const isBot = initialData?.isBot;

	// Для ботов / SSR не рендерим приватные lk-роуты
	const renderRoute = !isSSR || !isBot;

	const isAgency = userType === 'agency';
	const location = useLocation();
	const urlParams = location.pathname;
	const newTicket = useSelector(getIncomingTicket);
	const userId = useSelector(getUserID);
	const agencyId = useSelector(getAgencyID);

	/* SSR START */
	if (initialData?.isBot) {
		const parseDisabledFilters = (filters) => {
			const disabledFilters = [];
			const default_ranges = {
				age: { min: 0, max: 0 },
				price: { min: 0, max: 0 },
				height: { min: 0, max: 0 },
				weight: { min: 0, max: 0 },
				penis: { min: 0, max: 0 },
			};
			// Итерация по всем свойствам объекта
			Object.entries(filters).forEach(([key, value]) => {
				if (key.includes('_min')) {
					default_ranges[key.replace('_min', '')].min = value;
				}

				if (key.includes('_max')) {
					default_ranges[key.replace('_max', '')].max = value;
				}

				// Пропускаем вложенные объекты
				if (typeof value === 'object' && value !== null) {
					return;
				}

				// Если значение false — добавляем ключ
				if (value === false) {
					disabledFilters.push(key);
				}
			});

			const filterMapping = {
				// status
				statusHealthControl: 'statusHealthControl',
				statusIndividual: 'statusIndividual',

				// ethnos
				ethnosArabic: 'ethnosArabic',
				ethnosIndian: 'ethnosIndian',
				ethnosMulatto: 'ethnosMulatto',

				// penis
				penisSmall: 'penisSmall',

				// sex
				sexHomosexual: 'sexHomosexual',
				sexLesbian: 'sexLesbian',

				// special
				specialLipsPumped: 'specialLipsPumped',
				hairIntimHaircut: 'hairIntimHaircut',

				// place/status
				placeIncall: 'placeIncall',
				placeOutcall: 'placeOutcall',
				placeEscort: 'placeEscort',
				statusMassage: 'statusMassage', // для eromassage-1

				// gender (если нужно)
				genderFemale: 'genderFemale',
				genderMale: 'genderMale',
				genderTrans: 'genderTrans',
				genderCouple: 'genderCouple',
			};

			const syncedDisabledFilters = disabledFilters.filter(
				(key) => filterMapping[key],
			);

			return { disabledFilters, syncedDisabledFilters, default_ranges };
		};
		if (initialData.services !== undefined) {
			const citiesData = initialData.services.data.cities;
			const languages = initialData.services.data.languages;
			const nationalities = initialData.services.data.nationalities;
			const services = initialData.services.data.services;
			dispatch(setLanguagesWords(languages));
			dispatch(setNationalistWords(nationalities));
			dispatch(setServicesWords(services));
			dispatch(setCountriesData(citiesData));
		}
		if (initialData.countrySlug !== undefined) {
			dispatch(setCountrySlug(initialData.countrySlug));
		}
		if (initialData.currentCity !== undefined) {
			dispatch(
				setCurrentCity({
					...initialData.currentCity,
					title: initialData.currentCity.name,
				}),
			);
		}
		if (initialData.currentDistrict !== undefined) {
			dispatch(setUserCurrentDistrict(initialData.currentDistrict));
		}
		if (initialData.titleArray !== undefined) {
			dispatch(setTitleArray(initialData.titleArray));
		} else {
			dispatch(clearTitle());
		}
		if (initialData.profile !== undefined) {
			dispatch(setProfileData(initialData.profile));
			const sortedServices = [...initialData.profile.services].sort(
				(a, b) => a.category_id - b.category_id,
			);
			dispatch(setSortedServices(sortedServices));
		}
		if (initialData.lang !== undefined) {
			dispatch(setLang(initialData.lang));
		}
		if (initialData.city !== undefined) {
			dispatch(setCity(initialData.city));
		}
		if (initialData.selections !== undefined) {
			dispatch(setSelections(initialData.selections));
		}
		if (initialData.region !== undefined) {
			dispatch(setRegion(initialData.region));
		}
		if (initialData.agencies !== undefined) {
			dispatch(setAgencies(initialData.agencies));
		}
		if (initialData.agency !== undefined) {
			dispatch(setAgency(initialData.agency));
		}
		if (initialData.posts !== undefined) {
			dispatch(setPosts(initialData.posts));
		}
		if (initialData.post !== undefined) {
			dispatch(setPost(initialData.post));
		}
		if (initialData.topProfiles !== undefined) {
			dispatch(setTopProfilesData(initialData.topProfiles));
		}

		if (initialData.meta !== undefined) {
			dispatch(setAllModelsMeta(initialData.meta));
		}
		if (initialData.disabledMeta !== undefined) {
			dispatch(setDisabledModelsMeta(initialData.disabledMeta));
		}
		if (initialData.profiles !== undefined) {
			dispatch(setAllModels(initialData.profiles));
		}
		if (initialData.disabledProfiles !== undefined) {
			dispatch(setDisabledModels(initialData.disabledProfiles));
		}
		if (initialData.massage !== undefined) {
			if (initialData.massage) {
				dispatch(toggleSomeFilter('service_type', 'massage'));
			}
		}

		if (initialData.fastFilters !== undefined) {
			// HAVE NO IDEA WHY EXISTS PROBABLY DEPRECATED ALREADY
			// dispatch(setFastFilter(initialData.fastFilters.length > 0));
		}

		if (initialData.filters !== undefined) {
			const filterData = parseDisabledFilters(initialData.filters);

			// setDefaultRanges and disabled filters here
			dispatch(setDisabledFilters(filterData.disabledFilters));
			dispatch(setDefaultRangeState(filterData.default_ranges));
		}

		if (initialData.seo !== undefined) {
			dispatch(setSeoTitle(initialData.seo?.seo_title));
			dispatch(setSeoDescription(initialData.seo?.seo_description));
			dispatch(setSeoContent(initialData.seo?.content));
		} else {
			dispatch(setSeoTitle(false));
			dispatch(setSeoDescription(false));
			dispatch(setSeoContent(false));
		}
	}
	/* SSR END */

	const fetchMeData = useCallback(async () => {
		try {
			const route = 'user/me';
			const method = 'GET';

			const { data = {} } = await makeRequest({ route, method });
			const userType = data.user_type;
			const userId = data.id;
			const video = data.accept_video;
			const agency_id = data?.agency_id;
			const order = data.accept_booking;
			const balance = Math.round(data.balance);
			const favorites = data.favorites_count;
			const video_chats = data.video_chats_count;
			const bookings = data.bookings_count;
			const previews = data.previews_count;
			const hold = data.balance_hold;
			const tickets = data.tickets_count;
			const emailVerifyAt = data.email_verified_at;
			const profiles_status = data.profiles_status;

			dispatch(setUserAgencyId(agency_id));
			dispatch(setVideochatAmount(video_chats));
			dispatch(setBookingAmount(bookings));
			dispatch(setPreviewsAmount(previews));
			dispatch(setUserType(userType));
			dispatch(setUserId(userId));
			dispatch(setUserTariff(data.tariff));
			dispatch(setUserTariffSlug(data.tariff.data.slug));
			dispatch(setApiUserReady(true));
			dispatch(setReadyToOrder(order));
			dispatch(setReadyToVideochat(video));
			dispatch(setUserBalance(balance));
			dispatch(setUserHold(hold));
			dispatch(setFavoriteCounter(favorites));
			dispatch(setUserTickets(tickets));
			dispatch(setEmailVerifyAt(emailVerifyAt));
			dispatch(setProfileStatus(profiles_status ?? false));
		} catch (error) {
			console.error(error);
		}
	}, [dispatch]);

	useEffect(() => {
		if (
			urlParams.includes('videochats') ||
			urlParams.includes('videochat')
		) {
			dispatch(setVideochatStatus(true));
		}
	}, []);

	useEffect(() => {
		if (urlParams.includes('lk')) {
			dispatch(setIsLkPage(true));
		} else {
			dispatch(setIsLkPage(false));
		}
	}, [urlParams, navigate, dispatch]);

	useEffect(() => {
		if (lang && urlParams.includes('payment')) {
			navigate(`/${lang}/lk/topup`);
		}
	}, [lang, navigate, urlParams]);

	// WS SETUP FOR NOTIFICATIONS
	useEffect(() => {
		if (
			token &&
			(userId || agencyId) &&
			process.env.REACT_APP_WEBSOCKET_ENABLED
		) {
			let cleanup = false;

			import('@/services/initWebSocket').then(({ initWebSocket }) => {
				if (cleanup) return;
				initWebSocket(token).then(() => {
					if (cleanup) return;
					import('@/services/socketEventHandlers').then(
						({ registerEvents }) => {
							if (cleanup) return;
							registerEvents({
								dispatch,
								userId,
								agencyId,
								isAgency,
								userType,
								t,
							});
						},
					);
				});
			});

			return () => {
				cleanup = true;
			};
		}
	}, [dispatch, token, userId, agencyId, userType]);

	useEffect(() => {
		if (userType === 'default') {
			if (token) fetchMeData();
		} else {
			fetchMeData();
		}
	}, [token, userType, newTicket, fetchMeData]);

	useEffect(() => {
		const userAuth = localStorage.getItem('user');
		if (!userAuth && userType !== 'default') {
			dispatch(setUserType('default'));
			dispatch(setUserStatus('silver'));
			dispatch(setUserId(null));
			dispatch(setFavoriteCounter(0));
			//navigate(`/`);
		}
	}, [userType, dispatch]);

	// theme switch
	useEffect(() => {
		switch (theme) {
			case 'light': {
				document.body.classList.add('light');
				break;
			}
			case 'dark': {
				document.body.classList.remove('light');
				break;
			}
			default: {
				break;
			}
		}
	}, [theme]);

	return (
		<Suspense fallback={<Loader height={100} />}>
			<ScrollToTop />
			<Routes>
				<Route
					path="/"
					element={
						<FilterLogicProvider>
							<MainLayout initialData={initialData} />
						</FilterLogicProvider>
					}
				>
					<Route
						path=":lang?/:city?/:params?"
						element={<MainPage initialData={initialData} />}
					/>

					<Route
						path=":lang?/registration"
						element={<RegistrationPage />}
					/>

					<Route
						path=":lang?/support"
						element={<SupportPage />}
					/>

					<Route
						path=":lang?/guide"
						element={<Guide />}
					/>

					<Route
						path=":lang?/blog"
						element={<Blogs />}
					/>

					<Route
						path=":lang?/arch"
						element={<GlobalBlockPage />}
					/>

					<Route
						path=":lang?/blog/:slug"
						element={<Blog />}
					/>

					<Route
						path=":lang?/gdpr"
						element={<PrivacyPolicy />}
					/>

					<Route
						path=":lang?/rules"
						element={<RulesPage />}
					/>

					<Route
						path=":lang?/provisions"
						element={<ProvisionsPage />}
					/>

					<Route
						path=":lang?/about"
						element={<About />}
					/>

					<Route
						path=":lang?/uikit"
						element={<UIKitPage />}
					/>

					<Route
						path=":lang?/login/:token"
						element={<AdminLogin />}
					/>

					<Route
						path=":lang?/agencies"
						element={<AgencyList />}
					/>

					<Route
						path=":lang?/massagebrothels"
						element={<MassageList />}
					/>

					<Route
						path=":lang?/history"
						element={<History />}
					/>

					<Route
						path=":lang?/filter/:uuid"
						element={<MainPage />}
					/>

					<Route
						path=":lang?/profile/:profileSlug"
						element={
							<Suspense fallback={<Loader />}>
								<VariantModelPage />
							</Suspense>
						}
					/>

					<Route
						path=":lang?/redirect/:way"
						element={<Loader></Loader>}
					/>

					<Route
						path=":lang?/check-:profileSlug"
						element={<VariantModelPage />}
					/>

					<Route
						path=":lang?/operator-confirm/:key"
						element={<OperatorConfirm />}
					/>

					<Route
						path=":lang?/operator-confirm/:key"
						element={<OperatorConfirm />}
					/>

					<Route
						path=":lang/agency/:agencyId/:params?"
						element={<AgencyPage />}
					/>

					<Route
						path=":lang?/party/:partyId"
						element={<PartyPage />}
					/>

					<Route
						path=":lang?/call/:room"
						element={<MainPage />}
					/>

					<Route
						path=":lang?/selection/:city"
						element={<Selection />}
					/>

					{/* lk pages START ---------------------------------------------------  */}
					<Route
						path=":lang?/lk"
						element={<RedirectLk />}
					/>

					<Route
						path=":lang?/lk/photograph"
						element={<ComingSoon />}
					/>

					<Route
						path=":lang?/lk/support"
						element={<SupportPage />}
					/>

					<Route
						path=":lang?/lk/balance"
						element={
							<PrivateRoute>
								<BalancePage />
							</PrivateRoute>
						}
					/>

					<Route
						path=":lang?/lk/previews"
						element={
							<PrivateRoute>
								<PreviewsPage />
							</PrivateRoute>
						}
					/>
					<Route
						path=":lang?/lk/withdraw"
						element={
							<PrivateRoute>
								<WitchdrawBalance />
							</PrivateRoute>
						}
					/>
					<Route
						path=":lang?/lk/profiles"
						element={
							<PrivateRoute>
								<ProfilesPage />
							</PrivateRoute>
						}
					/>
					<Route
						path=":lang?/lk/operators"
						element={
							<PrivateRoute>
								<OperatorsPage />
							</PrivateRoute>
						}
					/>
					<Route
						path=":lang?/lk/parties"
						element={
							<PrivateRoute>
								<PartiesPage />
							</PrivateRoute>
						}
					/>
					<Route
						path=":lang?/lk/party/:partyid"
						element={
							<PrivateRoute>
								<ChangePartyPage />
							</PrivateRoute>
						}
					/>
					<Route
						path=":lang?/lk/parties/admin"
						element={
							<PrivateRoute>
								<PartiesPage />
							</PrivateRoute>
						}
					/>

					<Route
						path=":lang?/party/:id"
						element={
							<PrivateRoute>
								<PartyPage />
							</PrivateRoute>
						}
					/>

					<Route
						path=":lang?/lk/videochats"
						element={
							<PrivateRoute>
								<VideochatHistoryPage />
							</PrivateRoute>
						}
					/>
					<Route
						path=":lang?/lk/reviews"
						element={
							<PrivateRoute>
								<ReviewsPage />
							</PrivateRoute>
						}
					/>
					<Route
						path=":lang?/lk/reservations"
						element={
							<PrivateRoute>
								<ReservationPage />
							</PrivateRoute>
						}
					/>
					<Route
						path=":lang?/lk/statistic/:slug/:name"
						element={
							<PrivateRoute>
								<StatisticPage />
							</PrivateRoute>
						}
					/>
					<Route
						path=":lang?/lk/statistic"
						element={
							<PrivateRoute>
								<StatisticPage />
							</PrivateRoute>
						}
					/>
					<Route
						path={`:lang?/lk/topup`}
						element={
							<PrivateRoute>
								<Balance />
							</PrivateRoute>
						}
					/>
					<Route
						path={`:lang?/lk/:userType/balance`}
						element={
							<PrivateRoute>
								<Balance />
							</PrivateRoute>
						}
					/>
					{/* lk pages END ---------------------------------------------------  */}

					{/* lk agency START ---------------------------------------------------  */}
					<Route
						path=":lang?/lk/agency"
						element={
							<PrivateRoute>
								<AccountPage />
							</PrivateRoute>
						}
					/>
					{/* lk agency END ---------------------------------------------------  */}

					<Route
						path=":lang?/lk/photographer"
						element={
							<PrivateRoute>
								<ComingSoon />
							</PrivateRoute>
						}
					/>

					{/* lk customer START ---------------------------------------------------  */}
					<Route
						path=":lang?/lk/customer"
						element={
							<PrivateRoute>
								<AccountPage />
							</PrivateRoute>
						}
					/>

					<Route
						path=":lang?/lk/customer/subscriptions"
						element={
							<PrivateRoute>
								<Subscriptions />
							</PrivateRoute>
						}
					/>
					{/* lk customer END ---------------------------------------------------  */}

					{/* lk indi START ---------------------------------------------------  */}
					<Route
						path=":lang?/lk/indi"
						element={
							<PrivateRoute>
								<AccountPage />
							</PrivateRoute>
						}
					/>
					<Route
						path=":lang?/lk/profile/:profileSlug/edit"
						element={
							<PrivateRoute>
								<VariantModelPage edit />
							</PrivateRoute>
						}
					/>
					{/*  lk indi END --------------------------------------------------- */}
					<Route
						path=":lang?/lk/create"
						element={
							<PrivateRoute>
								<CreateProfile />
							</PrivateRoute>
						}
					/>
					<Route
						path=":lang?/lk/create/:slug"
						element={
							<PrivateRoute>
								<CreateProfile />
							</PrivateRoute>
						}
					/>

					<Route
						path=":lang?/preview/:profileSlug"
						element={
							<PrivateRoute>
								<VariantModelPage edit />
							</PrivateRoute>
						}
					/>

					<Route
						path={`:lang?/signin/:token`}
						element={
							<PrivateRoute>
								<SignIn />
							</PrivateRoute>
						}
					/>

					{renderRoute && (
						<>
							<Route
								path=":lang?/links"
								element={<PartnerLinksPage />}
							/>
						</>
					)}
				</Route>
				<Route
					path="404"
					element={<ErrorPage />}
				/>
				<Route
					path="*"
					element={<ErrorPage />}
				/>
			</Routes>
			<ActivateProfilePopup />
		</Suspense>
	);
}

export default App;
